import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { FaStore, FaUpload, FaCog, FaCheck, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  openStore,
  closeStore,
  getStoreAvailability,
  createPedidosYaCatalogFromLocalId
} from '../../../../../../../../crud/pedidos_api/pedidos_ya';
import {
  initializeTPPConfig,
  getTPPConfig
} from '../../../../../../../../crud/management_api/third_party_provider_config';
import AdvancedStoreInfo from './components/AdvancedStoreInfo/AdvancedStoreInfo';
import './PedidosYa.scss';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';

const PedidosYa: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [storeStatus, setStoreStatus] = useState<any>(null);
  const [configData, setConfigData] = useState<any>(null);
  const [configNotFound, setConfigNotFound] = useState(false);
  const [storeNotFound, setStoreNotFound] = useState(false);
  const [catalogStatus, setCatalogStatus] = useState<any>(null);
  
  // Section visibility states
  const [storeExpanded, setStoreExpanded] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  
  // Form states for configuration
  const [platformRestaurantId, setPlatformRestaurantId] = useState('');
  const [posVendorId, setPosVendorId] = useState('');
  const [remoteId, setRemoteId] = useState('');
  const [chainCode, setChainCode] = useState('');
  const [integrationVendorId, setIntegrationVendorId] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = user.local_id;

  const renderProviderLogo = () => {
    return(
      <div className="provider-logo-small">
        <img 
          alt="PedidosYa logo"
          className="provider-logo-img"
          src={`${process.env.PUBLIC_URL}/icons/providers/pedidos-ya.png`} 
        />
      </div>
    )
  }

  const loadStoreStatus = useCallback(async () => {
    try {
      const response = await getStoreAvailability(localId);
      setStoreStatus(response.data);
      setStoreNotFound(false);
    } catch (error: any) {
      console.error('Error loading store status:', error);
      if (error.response?.status === 404) {
        setStoreNotFound(true);
        setStoreStatus(null);
      } else {
        toast.error('Error cargando estado de la tienda');
      }
    }
  }, [localId]);

  const loadConfiguration = useCallback(async () => {
    try {
      const response: any = await getTPPConfig({
        local_id: localId,
        third_party_provider_id: 3 // PedidosYa
      });
      
      if (response.data) {
        setConfigData(response.data);
        setConfigNotFound(false);
        const config = response.data.config?.object || {};
        setPlatformRestaurantId(config.platform_restaurant_id || '');
        setPosVendorId(config.pos_vendor_id || '');
        setRemoteId(config.remote_id || '');
        setChainCode(config.chain_code || '');
        setIntegrationVendorId(config.integration_vendor_id || '');
      }
    } catch (error: any) {
      console.error('Error loading configuration:', error);
      if (error.response?.status === 404) {
        setConfigNotFound(true);
        setConfigData(null);
      } else {
        toast.error('Error cargando configuración');
      }
    }
  }, [localId]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (isExpanded) {
        setLoading(true);
        try {
          await Promise.all([
            loadStoreStatus(),
            loadConfiguration()
          ]);
        } catch (error) {
          console.error('Error loading PedidosYa data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]); // loadStoreStatus and loadConfiguration are stable callbacks

  const handleStoreToggle = async (open: boolean) => {
    setLoading(true);
    try {
      if (open) {
        await openStore(localId);
        toast.success('Tienda abierta en PedidosYa');
      } else {
        await closeStore(localId);
        toast.success('Tienda cerrada en PedidosYa');
      }
      
      // Update store status immediately without API call
      if (storeStatus && storeStatus.platforms) {
        const updatedPlatforms = storeStatus.platforms.map((platform: any) => {
          if (platform.platformKey === 'PY') {
            return {
              ...platform,
              availabilityState: open ? 'OPEN' : 'CLOSED'
            };
          }
          return platform;
        });
        
        setStoreStatus({
          ...storeStatus,
          platforms: updatedPlatforms
        });
      }
      
      // Still reload to get any additional data that might have changed
      await loadStoreStatus();
    } catch (error: any) {
      console.error('Error toggling store:', error);
      toast.error(`Error ${open ? 'abriendo' : 'cerrando'} la tienda: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMenu = async () => {
    setLoading(true);
    try {
      const response = await createPedidosYaCatalogFromLocalId(localId);
      setCatalogStatus(response.data);
      toast.success('Menú actualizado exitosamente en PedidosYa');
    } catch (error: any) {
      console.error('Error updating menu:', error);
      toast.error(`Error actualizando menú: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!platformRestaurantId || !posVendorId || !remoteId) {
      toast.error('Los campos Platform Restaurant ID, POS Vendor ID y Remote ID son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const configPayload = {
        local_id: localId,
        third_party_provider_id: 3, // PedidosYa
        external_local_id: posVendorId,
        config: {
          platform_restaurant_id: platformRestaurantId,
          pos_vendor_id: posVendorId,
          remote_id: remoteId,
          chain_code: chainCode,
          integration_vendor_id: integrationVendorId
        }
      };

      await initializeTPPConfig(configPayload);
      toast.success('Configuración guardada exitosamente');
      setConfigNotFound(false);
      await loadConfiguration();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      toast.error(`Error guardando configuración: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStoreStatus = () => {
    if (storeNotFound || configNotFound) {
      return (
        <Alert variant="warning" className="mb-3">
          <strong>Configuración requerida:</strong> Para gestionar el estado de la tienda en PedidosYa, 
          primero necesitas crear la configuración de credenciales en la sección "Configuración".
        </Alert>
      );
    }

    if (!storeStatus) {
      return (
        <small className="text-muted">Estado de la tienda no disponible</small>
      );
    }

    // Check if we have the new API format with platforms array
    const platforms = storeStatus.platforms || [];
    const pedidosYaPlatform = platforms.find((p: any) => p.platformKey === 'PY');
    const isOpen = pedidosYaPlatform?.availabilityState === 'OPEN';
    
    return (
      <div className="store-status-section">
        <div className="store-controls mb-2">
          <button
            className={`mp-button ${isOpen ? 'danger' : 'success'}`}
            onClick={() => handleStoreToggle(!isOpen)}
            disabled={loading}
          >
            {loading ? (
              <MiniLoadingSpinner />
            ) : (
              <>
                {isOpen ? <FaTimes className="me-2" style={{color: 'var(--text-color)'}} /> : <FaCheck className="me-2" style={{color: 'var(--text-color)'}} />}
                {isOpen ? 'Cerrar Tienda' : 'Abrir Tienda'}
              </>
            )}
          </button>
        </div>


        <AdvancedStoreInfo storeStatus={storeStatus} />
      </div>
    );
  };

  const renderMenuSection = () => (
    <div className="menu-section">
      {configNotFound && (
        <Alert variant="warning" className="mb-3">
          <strong>Configuración requerida:</strong> Para actualizar el menú en PedidosYa, primero necesitas:
          <ol className="mb-0 mt-1">
            <li>Crear la configuración de credenciales en la sección "Configuración"</li>
            <li>Luego actualizar el menú, que tomará el menú de Medio Punto y lo cargará en PedidosYa</li>
          </ol>
        </Alert>
      )}
      
      <div className="menu-controls mb-2">
        <button
          className="mp-button primary me-2"
          onClick={handleUpdateMenu}
          disabled={loading || configNotFound}
        >
          {loading ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <FaUpload className="me-2" style={{color: 'var(--text-color)'}} />
          )}
          Actualizar Menú
        </button>
        {catalogStatus && (
          <small className="text-muted">
            Última actualización: {new Date(catalogStatus.timestamp || Date.now()).toLocaleString()}
          </small>
        )}
      </div>

      {catalogStatus && (
        <div className={`alert alert-${catalogStatus.submit_success ? 'success' : 'warning'} py-2`}>
          <small>
            <strong>Estado:</strong> {catalogStatus.submit_success ? 'Enviado exitosamente' : 'Error en envío'}
            {catalogStatus.catalog_stats && (
              <span className="ms-2">
                - Productos: {catalogStatus.catalog_stats.products}, 
                Categorías: {catalogStatus.catalog_stats.categories}
              </span>
            )}
          </small>
        </div>
      )}
    </div>
  );

  const renderConfigurationSection = () => (
    <div className="configuration-section">
      {configNotFound && (
        <Alert variant="warning" className="mb-3">
          <strong>No se encontró configuración:</strong> Necesitas crear una configuración para conectar con PedidosYa. 
          Completa los campos a continuación y guarda la configuración.
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Platform Restaurant ID *</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={platformRestaurantId}
                onChange={(e) => setPlatformRestaurantId(e.target.value)}
                placeholder="478829"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">POS Vendor ID *</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={posVendorId}
                onChange={(e) => setPosVendorId(e.target.value)}
                placeholder="CL-INTEGRACION-MEDIO-PUNTO-0001"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Remote ID *</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={remoteId}
                onChange={(e) => setRemoteId(e.target.value)}
                placeholder="CL-INTEGRACION-MEDIO-PUNTO-0001"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Chain Code</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={chainCode}
                onChange={(e) => setChainCode(e.target.value)}
                placeholder="CLMEDIOPUNTOTEST0001"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Integration Vendor ID</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={integrationVendorId}
                onChange={(e) => setIntegrationVendorId(e.target.value)}
                placeholder="INTEGRACIONN-MEDIO-PUNTO-0001"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="configuration-controls mt-2">
          <button
            className="mp-button success me-2"
            onClick={handleSaveConfiguration}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" className="me-2" />
            ) : (
              <FaCog className="me-2" style={{color: 'var(--text-color)'}} />
            )}
            Guardar
          </button>
        </div>
      </Form>

      {configData && (
        <div className="alert alert-success py-2 mt-2">
          <small>
            <FaCheck className="me-2" style={{color: 'var(--mediopunto-orange)'}} />
            Configurado desde: {new Date(configData.config?.timestamp || Date.now()).toLocaleString()}
          </small>
        </div>
      )}
    </div>
  );

  return (
    <div className="pedidos-ya-card">
      <div 
        className="pedidos-ya-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {renderProviderLogo()}
            <h4 className="mb-0 ms-4">PedidosYa</h4>
          </div>
          <div className="d-flex align-items-center">
            {configData && !configNotFound ? (
              <Badge bg="success" className="me-2">Configurado</Badge>
            ) : configNotFound ? (
              <Badge bg="warning" className="me-2">Sin Configurar</Badge>
            ) : null}
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="pedidos-ya-body">
          {loading && !storeStatus && !configData ? (
            <div className="text-center py-3">
              <MiniLoadingSpinner />
            </div>
          ) : (
            <div className="sections-container">
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setStoreExpanded(!storeExpanded)}
                >
                  <FaStore className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Estado de Tienda</span>
                  {storeNotFound || configNotFound ? (
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                  ) : storeStatus && storeStatus.platforms ? (
                    <Badge 
                      bg={storeStatus.platforms.find((p: any) => p.platformKey === 'PY')?.availabilityState === 'OPEN' ? 'success' : 'danger'} 
                      className="ms-auto me-2"
                    >
                      {storeStatus.platforms.find((p: any) => p.platformKey === 'PY')?.availabilityState === 'OPEN' ? 'ABIERTA' : 'CERRADA'}
                    </Badge>
                  ) : null}
                  <FaChevronDown className={`chevron-icon ${storeExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${storeExpanded ? 'expanded' : ''}`}>
                  {renderStoreStatus()}
                </div>
              </div>
              
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setMenuExpanded(!menuExpanded)}
                >
                  <FaUpload className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Actualizar Menú</span>
                  {configNotFound ? (
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                  ) : catalogStatus ? (
                    <Badge 
                      bg={catalogStatus.submit_success ? 'success' : 'warning'} 
                      className="ms-auto me-2"
                    >
                      {catalogStatus.submit_success ? 'Actualizado' : 'Pendiente'}
                    </Badge>
                  ) : null}
                  <FaChevronDown className={`chevron-icon ${menuExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${menuExpanded ? 'expanded' : ''}`}>
                  {renderMenuSection()}
                </div>
              </div>
              
              <div className="section-item">
                <div 
                  className="section-header"
                  onClick={() => setConfigExpanded(!configExpanded)}
                >
                  <FaCog className="me-3" style={{color: 'var(--text-color)'}} />
                  <span className="fw-medium">Configuración</span>
                  {configData && !configNotFound ? (
                    <Badge bg="success" className="ms-auto me-2">Configurado</Badge>
                  ) : configNotFound ? (
                    <Badge bg="warning" className="ms-auto me-2">Sin Configurar</Badge>
                  ) : null}
                  <FaChevronDown className={`chevron-icon ${configExpanded ? 'expanded' : ''}`} />
                </div>
                <div className={`section-content ${configExpanded ? 'expanded' : ''}`}>
                  {renderConfigurationSection()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PedidosYa;